















































































































































import Vue from "vue";
import moment from "moment";
import { mapGetters, mapState } from "vuex";
import { RootState } from "@/store";

import DateMomentTooltip from "@/components/DateMomentTooltip.vue";

import { AgentTunnelSession } from "@/model/data/remoteLink/AgentTunnelSession";
import { StartAgentTunnelSessionResData } from "@/model/communication/remoteLink/StartAgentTunnelSessionResData";
import { StartAgentTunnelSessionCommand } from "@/model/communication/remoteLink/StartAgentTunnelSessionCommand";
import { StopAgentTunnelSessionCommand } from "@/model/communication/remoteLink/StopAgentTunnelSessionCommand";
import { ApiResponseStatusEnum } from "@/model/communication/ApiResponseStatusEnum";
import { TunnelProtocolEnum, TunnelProtocolToPort } from "@/model/data/enums/TunnelProtocolEnum";
import { ApiGenericResponse } from "@/model/communication/ApiGenericResponse";
import { CReqExecutionResultEnum } from "@/model/communication/CReqExecutionResultEnum";
import { AgentInfo } from "@/model/data/AgentInfo";

export default Vue.extend({
    components: { DateMomentTooltip },
    props: {
        session: Object as () => AgentTunnelSession,
        installationName: String,
        loading: Boolean,
        agentInfo: Object as () => AgentInfo,
    },
    data() {
        return {
            moment,
            copyLabel: "Copy",
            hostAlpha: "0.87",
            portAlpha: "0.87",
            canCopy: window.isSecureContext,
            showUrl: false,
            startSessionLoading: false,
            stopSessionLoading: false,
            localSession: this.session,
            secureConnection: false,
            TunnelProtocolEnum,
            alertType: "",
            errorMessage: "",
            showAlert: false,
            loadingChart: false,
            dateListString: [],
            seriesConnectedTime: [],
            servicePort: 0,
            rules: {
                required: (value) => !!value || this.$t("remoteLink.servicePortRequired"),
                invalid: (value) => (value > 0 && value < 65535) || this.$t("remoteLink.invalidPort"),
                overlapping: (value) => {
                    if (value == TunnelProtocolToPort(TunnelProtocolEnum.SSH)) return this.$t("remoteLink.overlappingPort", { protocol: "SSH" });
                    if (value == TunnelProtocolToPort(TunnelProtocolEnum.HTTP) || value == TunnelProtocolToPort(TunnelProtocolEnum.HTTPS))
                        return this.$t("remoteLink.overlappingPort", { protocol: "WEB" });
                    if (value == TunnelProtocolToPort(TunnelProtocolEnum.CaosDB)) return this.$t("remoteLink.overlappingPort", { protocol: "CAOSDB" });
                    if (value == TunnelProtocolToPort(TunnelProtocolEnum.UltraVNC)) return this.$t("remoteLink.overlappingPort", { protocol: "ULTRAVNC" });
                    return true;
                },
            },
            copyIcon: "copy",
            cursorIcon: "i-cursor",
            circleIcon: "circle",
        };
    },
    computed: {
        ...mapGetters(["userDateFormat"]),
        ...mapState({
            startupData: (state: RootState) => state.application.startupData,
        }),
        host() {
            if (this.startupData && this.startupData.currentIp) return this.startupData.currentIp;
            return location.host;
        },
    },
    watch: {
        installationName: {
            handler() {
                this.showAlert = false;
                this.localSession = this.session;
                this.startSessionLoading = false;
                this.stopSessionLoading = false;
            },
            immediate: true,
        },
        session() {
            this.localSession = this.session;
        },
    },
    methods: {
        async copy(type: string) {
            switch (type) {
                case "endpoint":
                    await navigator.clipboard.writeText(`${this.host}:${this.localSession.port}`);
                    this.copyIcon = "check";
                    setTimeout(() => {
                        this.copyIcon = "copy";
                    }, 1000);
                    break;
                case "host":
                    await navigator.clipboard.writeText(this.host);
                    this.cursorIcon = "check";
                    setTimeout(() => {
                        this.cursorIcon = "i-cursor";
                    }, 1000);
                    break;
                case "port":
                    await navigator.clipboard.writeText(this.localSession.port);
                    this.circleIcon = "check";
                    setTimeout(() => {
                        this.circleIcon = "circle";
                    }, 1000);
                    break;
            }
        },
        async startSession() {
            this.showAlert = false;
            this.startSessionLoading = true;
            try {
                let response: StartAgentTunnelSessionResData = await new StartAgentTunnelSessionCommand(this.installationName, null, this.servicePort, false).execute();
                if (response.responseStatus == ApiResponseStatusEnum.Success && response.responseString.toLowerCase() == "success") {
                    this.localSession = response.newSessions[0];
                    this.$emit("change-name", this.localSession);
                } else {
                    throw response.message;
                }
            } catch (error) {
                this.showAlert = true;
                switch (error) {
                    case CReqExecutionResultEnum.AgentOffline:
                        this.errorMessage = this.$t("device_status.agentOfflineForInstallation", { task: "connect" });
                        this.alertType = "warning";
                        break;
                    case CReqExecutionResultEnum.AgentNotInstalled:
                        this.errorMessage = this.$t("device_status.agentNotInstalled", { task: "connect" });
                        this.alertType = "error";
                        break;
                    case CReqExecutionResultEnum.DeviceLocked:
                        this.errorMessage = this.$t("device_status.agentLocked", { task: "connect" });
                        this.alertType = "warning";
                        break;
                    case CReqExecutionResultEnum.OperationNotAvailable:
                        this.errorMessage = this.$t("device_status.operationNotAvailable", { task: "connection" });
                        this.alertType = "info";
                        break;
                    case CReqExecutionResultEnum.ServerError:
                        this.errorMessage = this.$t("device_status.serverError");
                        this.alertType = "error";
                        break;
                    case CReqExecutionResultEnum.InvalidRequest:
                        this.errorMessage = this.$t("remoteLink.invalidVersion");
                        this.alertType = "error";
                        break;
                    case CReqExecutionResultEnum.ConnectionLimitReached:
                        this.errorMessage = this.$t("remoteLink.portAlreadyForwarded");
                        this.alertType = "warning";
                        break;
                    default:
                        console.error("Something went wrong sending Connect request ", error);
                        this.errorMessage = this.$t("device_status.serverError");
                        this.alertType = "error";
                        break;
                }
            }
            this.startSessionLoading = false;
        },
        async stopSession(session: AgentTunnelSession) {
            this.stopSessionLoading = true;
            this.showAlert = false;
            try {
                let response: ApiGenericResponse = await new StopAgentTunnelSessionCommand(this.installationName, session.servicePort).execute();

                if (response.responseStatus == ApiResponseStatusEnum.Success && response.responseString.toLowerCase() == "success") {
                    this.localSession = null;
                    this.servicePort = 0;
                    this.$emit("remove-session");
                } else {
                    throw response.message;
                }
            } catch (error) {
                this.showAlert = true;
                switch (error) {
                    case CReqExecutionResultEnum.AgentOffline:
                        this.rebootErrorMessage = this.$t("device_status.agentOfflineForInstallation", { task: "disconnect" });
                        this.alertType = "warning";
                        break;
                    case CReqExecutionResultEnum.AgentNotInstalled:
                        this.rebootErrorMessage = this.$t("device_status.agentNotInstalled", { task: "disconnect" });
                        this.alertType = "error";
                        break;
                    case CReqExecutionResultEnum.DeviceLocked:
                        this.rebootErrorMessage = this.$t("device_status.agentLocked", { task: "disconnect" });
                        this.alertType = "warning";
                        break;
                    case CReqExecutionResultEnum.OperationNotAvailable:
                        this.rebootErrorMessage = this.$t("device_status.operationNotAvailable", { task: "disconnection" });
                        this.alertType = "info";
                        break;
                    case CReqExecutionResultEnum.ServerError:
                        this.rebootErrorMessage = this.$t("device_status.serverError");
                        this.alertType = "error";
                        break;
                    case CReqExecutionResultEnum.InvalidRequest:
                        this.rebootErrorMessage = this.$t("remoteLink.invalidVersion");
                        this.alertType = "error";
                        break;
                    default:
                        console.error("Something went wrong sending Connect request ", error);
                        this.errorMessage = this.$t("device_status.serverError");
                        this.alertType = "error";
                        break;
                }
            }
            this.stopSessionLoading = false;
        },
    },
});
